// Filter conditions a dashboard filter can use. The codes are what the API reads (and what
// saved configs store), so they never change; users only ever see the labels.

// Offered in the condition dropdown, in this order.
export const DASHBOARD_FILTER_OPERATORS = [
    'eq',
    'neq',
    'gt',
    'gte',
    'lt',
    'lte',
    'between',
    'contains',
    'not_contains',
    'starts_with',
    'ends_with',
    'in',
    'not_in',
] as const;

// No longer offered — contains / does not contain replace them — but saved configs may still
// use them. The API passes a `like` value through raw, so it's an exact match unless the
// value carries its own % wildcards.
const DASHBOARD_LEGACY_FILTER_OPERATORS = ['like', 'not_like'] as const;

export type DashboardFilterOperator =
    | (typeof DASHBOARD_FILTER_OPERATORS)[number]
    | (typeof DASHBOARD_LEGACY_FILTER_OPERATORS)[number];

export const DASHBOARD_FILTER_OPERATOR_LABELS: Record<DashboardFilterOperator, string> = {
    eq: '=',
    neq: '≠',
    gt: '>',
    gte: '≥',
    lt: '<',
    lte: '≤',
    between: 'between',
    contains: 'contains',
    not_contains: 'does not contain',
    starts_with: 'starts with',
    ends_with: 'ends with',
    in: 'is one of',
    not_in: 'is not one of',
    like: 'matches pattern',
    not_like: 'does not match pattern',
};

// The dropdown's options for a filter: the offered ones, plus its own operator when that's a
// legacy one, so a saved filter still shows (and keeps) what it was built with.
export const filterOperatorOptions = (
    current?: DashboardFilterOperator,
): DashboardFilterOperator[] =>
    current && !(DASHBOARD_FILTER_OPERATORS as readonly string[]).includes(current)
        ? [...DASHBOARD_FILTER_OPERATORS, current]
        : [...DASHBOARD_FILTER_OPERATORS];

export const isArrayOperator = (op: DashboardFilterOperator): boolean =>
    op === 'in' || op === 'not_in';

// The operators whose value is picked as chips from the field's own values. The API takes a
// list for each: one that includes matches any of the values, one that excludes matches none.
// The range operators compare against one value, and the legacy pattern ones keep the raw
// pattern they were saved with. contains / starts with / ends with take one value: each is a
// LIKE (see toQueryFilter), and the API ANDs its filters, so it can't match any of several.
// does not contain can take several, since matching none of them is one not_like each.
const MULTI_VALUE_OPERATORS: DashboardFilterOperator[] = [
    'eq',
    'neq',
    'not_contains',
    'in',
    'not_in',
];

export const isMultiValueOperator = (op: DashboardFilterOperator): boolean =>
    MULTI_VALUE_OPERATORS.includes(op);

// Operators that compare whole values. Their match is exact, case included, so a typed value
// takes the casing of the stored one it names (see withOptionCasing). The text searches
// (contains etc.) keep what was typed.
export const isExactMatchOperator = (op: DashboardFilterOperator): boolean =>
    op === 'eq' || op === 'neq' || op === 'in' || op === 'not_in';

// Each value spelt as the suggested value it equals ignoring case, where there is one: typing
// "leeds" picks "Leeds" rather than a filter that matches nothing.
export const withOptionCasing = (values: string[], options: string[]): string[] => {
    const byLower = new Map(options.map(o => [o.toLowerCase(), o]));
    return values.map(v => byLower.get(v.trim().toLowerCase()) ?? v);
};

// A filter's value as the chips it shows.
export const filterValueToList = (value: string | number | string[] | null | undefined): string[] =>
    (Array.isArray(value) ? value : value == null || value === '' ? [] : [value])
        .map(v => String(v).trim())
        .filter(Boolean);

// The chips as a filter value. in/not_in always send a list; the others send a lone value as
// itself, so a single-value filter reads (and saves) exactly as it did before chips.
export const listToFilterValue = (
    list: string[],
    op: DashboardFilterOperator,
): string | number | string[] => {
    const values = [...new Set(list.map(v => v.trim()).filter(Boolean))];
    if (isArrayOperator(op)) return values;
    if (values.length === 0) return '';
    if (values.length === 1) return textToFilterValue(values[0], op);
    return values;
};

// A value carried across an operator switch: into a chip operator each value becomes a chip;
// otherwise it goes through the text the value box would show.
export const convertFilterValue = (
    value: string | number | string[],
    to: DashboardFilterOperator,
): string | number | string[] =>
    isMultiValueOperator(to)
        ? listToFilterValue(filterValueToList(value), to)
        : textToFilterValue(filterValueToText(value), to);

// The operators that exclude what they match, so a list pasted into one becomes "is not one of".
export const isNegatedOperator = (op: DashboardFilterOperator): boolean =>
    op === 'neq' || op === 'not_like' || op === 'not_contains' || op === 'not_in';

export const filterValueToText = (value: string | number | string[] | null | undefined): string =>
    Array.isArray(value) ? value.join(', ') : String(value ?? '');

// Parses the value box's text for an operator. `between` takes exactly two values (the API
// rejects anything else), so "from, to" splits into a pair, padding a missing bound with ''.
export const textToFilterValue = (
    text: string,
    op: DashboardFilterOperator,
): string | number | string[] => {
    if (isArrayOperator(op)) {
        return text
            .split(',')
            .map(v => v.trim())
            .filter(Boolean);
    }
    if (op === 'between') {
        const [from = '', to = ''] = text.split(',').map(v => v.trim());
        return [from, to];
    }
    // A text search matches characters, so "007" stays "007".
    if (op in WILDCARD_PATTERNS) return text;
    return /^-?\d+(\.\d+)?$/.test(text.trim()) ? Number(text.trim()) : text;
};

// The text searches the API has no operator for: each goes out as a LIKE with the wildcards
// added, so users never type `%` themselves. The API's LIKE has no ESCAPE clause, so a `%` or
// `_` in the value still acts as a wildcard.
const WILDCARD_PATTERNS = {
    contains: { operator: 'like', pattern: (v: string) => `%${v}%` },
    not_contains: { operator: 'not_like', pattern: (v: string) => `%${v}%` },
    starts_with: { operator: 'like', pattern: (v: string) => `${v}%` },
    ends_with: { operator: 'like', pattern: (v: string) => `%${v}` },
} as const;

export type DashboardQueryFilter = {
    field: string;
    operator: Exclude<DashboardFilterOperator, keyof typeof WILDCARD_PATTERNS>;
    value: string | number | string[];
};

// A filter as the API reads it. A text search with several values (does not contain, or one
// saved while the others took chips) becomes one LIKE per value, which the API ANDs.
export const toQueryFilter = ({
    field,
    operator,
    value,
}: {
    field: string;
    operator: DashboardFilterOperator;
    value: string | number | string[];
}): DashboardQueryFilter[] => {
    if (
        operator === 'contains' ||
        operator === 'not_contains' ||
        operator === 'starts_with' ||
        operator === 'ends_with'
    ) {
        const { operator: like, pattern } = WILDCARD_PATTERNS[operator];
        return filterValueToList(value).map(v => ({ field, operator: like, value: pattern(v) }));
    }
    return [{ field, operator, value }];
};
